import { useState, useEffect, useMemo, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  Edit,
  Trash2,
  Eye,
  Tag,
  ShoppingBag,
  CheckCircle,
  XCircle,
  Star,
  MessageSquare,
  Layers,
  Image as ImageIcon,
  Plus,
  Calendar,
  Settings,
  SlidersHorizontal,
  ClipboardList,
  Clock,
  FileText,
  Info
} from 'lucide-react';
import { CommonTable } from '../../components/CommonTable';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
import AddProductForm from './AddProductForm';
import { useAppDispatch, useAppSelector } from '../../store/hooks/reduxHooks';
import { UserRole } from '../../types/auth';
import {
  fetchProductsThunk,
  setPage,
  setPageSize,
  setSearchQuery,
  setSortBy,
  setSortOrder,
} from '../../features/products/productSlice';
import { Product, createProduct, updateProduct, deleteProduct } from '../../services/product';
import { showToast } from '../../utils/toast';
import { getChangedFields } from '../../utils/formUtils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { validateCustomerSearchInput } from "../../utils/searchValidator";
import { sub } from 'date-fns';

// Column ID type to ensure column IDs are always strings
type ColumnId =
  | 'srNo'
  | 'name'
  | 'brandName'
  | 'partnerName'
  | 'category'
  | 'subCategory '
  | 'variations'
  | 'updatedAt';

// Enum for column visibility
type ColumnVisibility = Record<ColumnId, boolean>;

const ProductsPage = () => {
  // Get current user role to determine permissions
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);

  const {
    products,
    loading,
    page,
    pageSize,
    total,
    sortBy,
    sortOrder,
    searchQuery,
    hasFetched,
  } = useAppSelector(state => state.products);
  const currentUserRole = user?.role || UserRole.CLEVERPE_USER;
  const [localSearch, setLocalSearch] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchValid, setIsSearchValid] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchInputChange = (value: string) => {
    setLocalSearch(value);

    // 🧹 Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // ✅ When input is cleared
    if (!value.trim()) {
      setSearchError(null);
      setIsSearchValid(true);

      dispatch(setPage(1));
      dispatch(setSearchQuery("")); // reset immediately
      return;
    }

    // ✅ Validation
    const result = validateCustomerSearchInput(value);
    setIsSearchValid(result.isValid);
    setSearchError(result.message || null);

    if (!result.isValid) return;

    // ⏱️ Delay API trigger by 3–4 ms
    searchTimeoutRef.current = setTimeout(() => {
      dispatch(setPage(1));
      dispatch(setSearchQuery(value)); // 🔥 API trigger
    }, 4); // 👈 3–4 ms
  };




  const handleSearchSubmit = () => {
    // If empty, already handled in change
    if (!localSearch.trim()) return;

    if (!isSearchValid) return;

    dispatch(setPage(1));
    dispatch(setSearchQuery(localSearch));
  };

  const handleResetSearch = () => {
    setLocalSearch("");    // clears input
    setSearchError(null);  // clears error
    setIsSearchValid(false); // disables search button
  };

  const prevRef = useRef({
    page,
    pageSize,
    sortBy,
    sortOrder,
    searchQuery,
  });
  const { token } = useAppSelector((state) => state.auth);

  // Check if user can edit/delete products (needs only the manage-product permission)
  const canManageProducts = useMemo(() => {
    // Check if user has the specific manage-product permission
    const hasManageProductsPermission = user?.dashPermissions?.includes('manage-product') || false;

    // Only check for the permission, regardless of user role
    return hasManageProductsPermission;
  }, [user?.dashPermissions]);

  // Table state
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Add loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add state for active tab
  const [activeTab, setActiveTab] = useState<string>('details');

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Define all columns with explicit IDs and normal sizes
  const allColumns: ColumnDef<Product, any>[] = [
    {
      id: 'srNo' as ColumnId,
      header: 'Sr. No',
      size: 90,
      cell: ({ row }) => {
        // Calculate serial number based on pagination
        const rowIndex = row.index;
        const serialNumber = (page - 1) * pageSize + rowIndex + 1;
        return <span className="text-sm font-medium">{serialNumber}</span>;
      },
    },
    {
      id: 'name' as ColumnId,
      header: 'Product Name',
      accessorKey: 'name',
      size: 250,
      cell: ({ row }) => {
        const productName = row.original.name;
        return (
          <div className="flex items-start">
            <ShoppingBag size={16} className="text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
            <div className="max-w-[200px]">
              <p className="text-sm font-medium truncate">
                {productName}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      id: 'brandName' as ColumnId,
      header: 'Brand',
      accessorKey: 'brandName',
      size: 180,
      cell: ({ row }) => (
        <span className="text-sm font-medium text-gray-700">
          {row.original.brandName}
        </span>
      ),
    },
    {
      id: 'partnerName' as ColumnId,
      header: 'Partner',
      accessorKey: 'partnerName',
      size: 180,
      cell: ({ row }) => (
        <span className="text-sm font-medium text-gray-700">
          {row.original.partnerName}
        </span>
      ),
    },
    {
      id: 'variations' as ColumnId,
      header: 'Variations',
      accessorKey: 'variations',
      size: 130,
      cell: ({ row }) => (
        <div className="flex items-center">
          <Layers size={16} className="text-purple-500 mr-2 flex-shrink-0" />
          <span className="text-sm">{row.original.variations.length}</span>
        </div>
      ),
    },
    {
      id: 'updatedAt' as ColumnId,
      header: 'Last Updated',
      accessorKey: 'updatedAt',
      size: 200,
      cell: ({ row }) => {
        const date = new Date(row.original.updatedAt);
        return (
          <div className="flex items-center">
            <Calendar size={16} className="text-gray-500 mr-2 flex-shrink-0" />
            <span className="text-sm">{date.toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</span>
          </div>
        );
      },
    },
  ];

  // Use all columns directly
  const visibleColumns = allColumns;

  /* ---------------- FIRST LOAD (ONLY ONCE) ---------------- */
  useEffect(() => {
    if (!hasFetched) return;

    const prev = prevRef.current;

    const hasChanged =
      prev.searchQuery !== searchQuery ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.sortBy !== sortBy ||
      prev.sortOrder !== sortOrder;

    if (!hasChanged) return;

    const timeout = setTimeout(() => {
      dispatch(fetchProductsThunk());

      // 🔑 update snapshot AFTER dispatch
      prevRef.current = {
        searchQuery,
        page,
        pageSize,
        sortBy,
        sortOrder,
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    searchQuery,
    page,
    pageSize,
    sortBy,
    sortOrder,
    hasFetched,
    dispatch,
  ]);



  useEffect(() => {
    if (!token) return;
    if (hasFetched) return;


    dispatch(fetchProductsThunk());

  }, [token, hasFetched, dispatch]);


  /* ---------------- HANDLERS ---------------- */

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));

  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch(setPageSize(newSize));
    dispatch(setPage(1));

  };

  const handleSearchChange = (query: string) => {
    dispatch(setSearchQuery(query));
    dispatch(setPage(1));

  };

  const handleSortChange = (columnId: string, order: 'asc' | 'desc') => {
    dispatch(setSortBy(columnId));
    dispatch(setSortOrder(order));

  };

  const handleRefresh = () => {
    dispatch(setPage(1));
    dispatch(fetchProductsThunk());
  };

  const handleRowClick = (product: Product) => {
    setSelectedProduct(product);
    setDetailsOpen(true);
  };

  // Handle closing details dialog
  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  // Handle edit product
  const handleEditProduct = (product: Product) => {
    console.log('Edit product clicked for:', product);

    if (!product || !product._id) {
      console.error('Cannot edit product: Invalid product data');
      showToast.error('Cannot edit product: Missing product data');
      return;
    }

    console.log('Setting edit mode with product ID:', product._id);
    setSelectedProduct(product);
    setIsEditing(true);
    setDetailsOpen(false);
    setAddProductOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (selectedProduct) {
      try {
        const response = await deleteProduct(selectedProduct._id);

        // Check if the deletion was successful
        if (response && response.status === "Request successful!") {
          // Show success toast
          showToast.success('Product deleted successfully');

          // Remove from local data
          dispatch(fetchProductsThunk());


          // Close dialogs
          setDeleteConfirmOpen(false);
          setDetailsOpen(false);
          setSelectedProduct(null);

          // Refetch products
          dispatch(fetchProductsThunk());
        } else {
          // Show error toast if response doesn't indicate success
          showToast.error('Failed to delete product');
        }
      } catch (error) {
        console.error('Error deleting product:', error);
        showToast.error('Failed to delete product. Please try again.');
      }
    }
  };

  // Handle form submission - now handles both add and edit
  const handleFormSubmit = async (formData: any) => {
    console.log('Form submitted in handleFormSubmit - start', { isEditing, formData });

    try {
      // Set loading state
      setIsSubmitting(true);

      console.log('Form submitted with data:', formData);
      console.log('Is editing mode:', isEditing);

      if (isEditing && selectedProduct) {
        console.log('Attempting to update product with ID:', selectedProduct._id);

        // Format product data for update - include only fields expected by the API
        const formattedEditData = {
          // Form fields that we update
          name: formData.name,
          modelCode: formData.modelCode || '', // Optional model code
          brandName: formData.brand,
          category: formData.category,
          subCategory: formData.subCategory,
          mrpCheck: formData.mrpCheck,
          serialNoValidationLevel: formData.serialNoValidationLevel,

          // Map variations to the correct format expected by the API
          variations: formData.variations.map((v: any) => {
            // Extract attribute entries to create features in the required format
            const features = Object.entries(v.attributes).map(([name, value]) => ({
              name,
              value: value as string
            }));

            return {
              name: Object.values(v.attributes).join(' - '),
              mrp: Number(v.mrp) || 0,
              mop: Number(v.price) || 0,
              features: features
            };
          })
        };

        // Create the original data object in the same format
        const originalData = {
          name: selectedProduct.name,
          modelCode: selectedProduct.modelCode || '',
          brandName: selectedProduct.brandName,
          category: selectedProduct.category,
          subCategory: selectedProduct.subCategory,
          mrpCheck: selectedProduct.mrpCheck || false,
          serialNoValidationLevel: selectedProduct.serialNoValidationLevel || 0,
          variations: selectedProduct.variations.map((v: any) => {
            return {
              name: v.name,
              mrp: v.mrp || 0,
              mop: v.mop || 0,
              features: v.features || []
            };
          })
        };

        // Get only the changed fields
        const productUpdateData = getChangedFields(originalData, formattedEditData);

        console.log('Original product data:', originalData);
        console.log('Edited product data:', formattedEditData);
        console.log('Only sending changed fields:', productUpdateData);

        // Only proceed if there are actually changes
        if (Object.keys(productUpdateData).length === 0) {
          showToast.info('No changes detected');
          setIsSubmitting(false);
          return;
        }

        // Call update API with the product ID in the query parameter
        const response = await updateProduct(selectedProduct._id, productUpdateData);
        console.log('Update API response:', response);

        if (response && response.status === "Request successful!") {
          showToast.success('Product updated successfully');

          // Update the selected product with new data
          const updatedProduct = { ...selectedProduct, ...productUpdateData };
          setSelectedProduct(updatedProduct);

          // Exit editing mode but keep details visible
          setIsEditing(false);

          // Refetch products to show updated data
          dispatch(fetchProductsThunk());

          // Close add/edit dialog but keep details dialog open
          setAddProductOpen(false);
          setDetailsOpen(true);
        } else {
          showToast.error('Failed to update product: ' + (response?.message || 'Unknown error'));
        }
      } else {
        // Handle adding a new product
        console.log('Creating new product - extracting form data');

        // Create a new product using the form data
        const newProductData = {
          name: formData.name,
          brandName: formData.brand,
          category: formData.category,
          modelCode: formData.modelCode || '', // Optional model code
          subCategory: formData.subCategory,
          mrpCheck: formData.mrpCheck,
          serialNoValidationLevel: formData.serialNoValidationLevel,
          partnerName: "CleverPe", // Default partner name
          schemeBased: false,
          schemes: [],

          // Map variations to the correct format for the API
          variations: formData.variations.map((v: any) => {
            // Only include variations that have attributes
            if (Object.keys(v.attributes).length === 0) return null;

            // Extract attribute entries to create features in the required format
            const features = Object.entries(v.attributes).map(([name, value]) => ({
              name,
              value: value as string
            }));

            return {
              name: Object.values(v.attributes).join(' - '),
              mrp: Number(v.mrp) || 0,
              mop: Number(v.price) || 0,
              features: features
            };
          }).filter(Boolean) // Remove null variations
        };

        console.log('Creating new product with data:', JSON.stringify(newProductData, null, 2));

        try {
          console.log('About to call createProduct API directly');

          // Get token from localStorage
          const token = localStorage.getItem('token') || '';

          // Log token (partially)
          if (token) {
            console.log(`Auth token available: ${token.substring(0, 10)}...`);
          } else {
            console.warn('No auth token found in localStorage!');
          }

          // Direct API call without the timeout to see if that's causing issues
          const response = await createProduct(newProductData);
          console.log('Create API response received:', response);

          // Check for both possible success status values
          if (response && (response.status === "Request successful!" || response.status === "Created!")) {
            showToast.success('Product created successfully');

            // Refetch products to show updated data
            dispatch(fetchProductsThunk());

            // Close dialog
            setAddProductOpen(false);
          } else {
            showToast.error('Failed to create product: ' + (response?.message || 'Unknown error'));
          }
        } catch (error: any) {
          console.error('Error during product creation API call:', error);
          showToast.error('Failed to create product: ' + (error?.message || 'API request failed'));
        }
      }
    } catch (error: any) {
      console.error('Error submitting product form:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to process product data';
      showToast.error(errorMessage);
    } finally {
      // Reset loading state
      setIsSubmitting(false);
      console.log('Form submission complete, loading state reset');
    }
  };


  return (
    <div className="w-full no-scrollbar">
      {/* Header section with Add Product button (only for admin/superadmin) */}
      <div className="flex justify-end items-center mb-4 mr-1">
        {canManageProducts && (
          <Button
            onClick={() => {
              setSelectedProduct(null);
              setIsEditing(false);
              setAddProductOpen(true);
            }}
            className="flex items-center gap-1"
            size="sm"
          >
            <Plus size={14} />
            <span>Add Product</span>
          </Button>
        )}
      </div>

      {/* Table container with horizontal scroll only for the table */}
      <div className="w-full rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <CommonTable
          columns={visibleColumns}
          data={products}
          isLoading={loading}
          isProductTab
          page={page}
          pageSize={pageSize}
          total={total}
          onRefresh={handleRefresh}
          onPageChange={(p) => dispatch(setPage(p))}
          onPageSizeChange={(size) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setPageSize(size));
          }}
          onSortChange={(field, order) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setSortBy(field));
            dispatch(setSortOrder(order));
          }}

          searchValue={localSearch}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          isSearchDisabled={!isSearchValid}
          searchError={searchError}
          onResetSearch={handleResetSearch} // ✅ new
          searchPlaceholder="Search products..."
          enableRowClick={true}
          onRowClick={handleRowClick}
          actions={[]}
        />

      </div>

      {/* Add Product Dialog */}
      <Dialog
        isOpen={addProductOpen}
        onClose={() => {
          if (!isSubmitting) {
            console.log('Closing product form dialog - resetting state');
            setAddProductOpen(false);
            setIsEditing(false);
            setSelectedProduct(null);
          }
        }}
        title={isEditing ? "Edit Product" : "Add New Product"}
        size="lg"
        preventClose={isSubmitting}
      >
        {/* Add custom direct submit handler */}
        <div
          className="w-full"
          onKeyDown={(e) => {
            // Listen for Enter key within the form
            if (e.key === 'Enter' && !isSubmitting) {
              console.log('Enter key detected in form, preventing default');
              e.preventDefault();
            }
          }}
        >
          <AddProductForm
            onSubmit={(formData) => {
              console.log('AddProductForm onSubmit called directly with data', formData);
              try {
                // Set loading state immediately
                setIsSubmitting(true);

                // Call handleFormSubmit synchronously, without setTimeout
                handleFormSubmit(formData);

                console.log('handleFormSubmit called successfully');
              } catch (error) {
                console.error('Error in onSubmit handler:', error);
                setIsSubmitting(false);
                showToast.error('An error occurred while processing the form');
              }
            }}
            onCancel={() => {
              if (!isSubmitting) {
                console.log('Cancel button clicked - resetting state');
                setAddProductOpen(false);
                setIsEditing(false);
                setSelectedProduct(null);
              }
            }}
            isSubmitting={isSubmitting}
            initialData={selectedProduct && isEditing ? {
              name: selectedProduct.name,
              modelCode: selectedProduct.modelCode || '', // Optional model code
              brand: selectedProduct.brandName,
              category: selectedProduct.category,
              subCategory: selectedProduct.subCategory,
              description: selectedProduct.name, // Use name as description since API doesn't store descriptions
              mrpCheck: selectedProduct.mrpCheck || false,
              serialNoValidationLevel: selectedProduct.serialNoValidationLevel || 0,
              // Map variations to form expected format
              variations: selectedProduct.variations.map(v => ({
                attributes: { type: v.name || 'Default' },
                mrp: v.mrp,
                price: v.mop,
                id: Math.random().toString(36).substr(2, 9)
              }))
            } : undefined}
          />
        </div>
      </Dialog>

      {/* Product Details Dialog */}
      <Dialog
        isOpen={detailsOpen}
        onClose={handleCloseDetails}
        title={selectedProduct ? `${selectedProduct.name} Details` : 'Product Details'}
        size="lg"
      >
        {selectedProduct && (
          <div className="space-y-6 no-scrollbar">
            {/* Dialog header with actions */}
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <ShoppingBag size={20} className="text-blue-500 mr-2" />
                <h2 className="text-lg font-medium">{selectedProduct.name}</h2>
              </div>
              <div className="flex space-x-2">
                {canManageProducts && (
                  <>
                    <Button
                      variant="outline"
                      color="red"
                      onClick={() => setDeleteConfirmOpen(true)}
                      className="flex items-center"
                      size="sm"
                    >
                      <Trash2 size={16} className="mr-1.5" />
                      Delete
                    </Button>
                    <Button
                      variant="default"
                      onClick={() => handleEditProduct(selectedProduct)}
                      className="flex items-center bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      <Edit size={16} className="mr-1.5" />
                      Edit
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Tabs Navigation */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList>
                <TabsTrigger value="details">
                  <div className="flex items-center">
                    <Info size={16} className="mr-1.5" />
                    Product Information
                  </div>
                </TabsTrigger>
                <TabsTrigger value="logs">
                  <div className="flex items-center">
                    <ClipboardList size={16} className="mr-1.5" />
                    Logs
                  </div>
                </TabsTrigger>
              </TabsList>

              {/* Product Details Tab */}
              <TabsContent value="details" className="space-y-6">
                {/* Product basic info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Product ID</h3>
                      <p className="mt-1 text-base font-mono">{selectedProduct._id}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Product Name</h3>
                      <p className="mt-1 text-base font-medium">{selectedProduct.name}</p>
                    </div>
                     <div>
                      <h3 className="text-sm font-medium text-gray-500">Model Number</h3>
                      <p className="mt-1 text-base font-medium">{selectedProduct.modelCode || "N/A"}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Brand</h3>
                      <p className="mt-1 text-base">{selectedProduct.brandName}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Partner</h3>
                      <p className="mt-1 text-base">{selectedProduct.partnerName}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Category</h3>
                      <p className="mt-1 text-base">{selectedProduct.category}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {selectedProduct.variations && selectedProduct.variations.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">MRP</h3>
                        <p className="mt-1 text-xl font-medium text-brand-blue">
                          {formatCurrency(selectedProduct.variations[0].mrp)}
                        </p>
                      </div>
                    )}
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Created At</h3>
                      <p className="mt-1 text-base">{new Date(selectedProduct.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
                      <p className="mt-1 text-base">{new Date(selectedProduct.updatedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Serial Number Validation Level</h3>
                      <p className="mt-1 text-base">{selectedProduct.serialNoValidationLevel}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Sub Category</h3>
                      <p className="mt-1 text-base">
                        {selectedProduct.subCategory || "N/A"}
                      </p>
                    </div>

                  </div>
                </div>

                {/* Product attributes/variations */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Variations</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.variations.map((variation, idx) => (
                      <span key={idx} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {variation.name || `Variation ${idx + 1}`}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Product metrics */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center text-green-700 mb-1">
                      <Layers size={16} className="mr-1" />
                      <h4 className="text-xs font-medium">VARIATIONS</h4>
                    </div>
                    <p className="text-2xl font-semibold">{selectedProduct.variations.length}</p>
                  </div>
                </div>
              </TabsContent>

              {/* Logs Tab */}
              <TabsContent value="logs" className="space-y-6">
                <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
                  <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700">Logs</h3>
                  </div>

                  {/* Using logs from API response */}
                  {selectedProduct.logs && selectedProduct.logs.length > 0 ? (
                    <div className="divide-y divide-gray-200 max-h-[400px] overflow-y-auto">
                      {selectedProduct.logs.slice().reverse().map((log, index) => (
                        <div key={index} className="p-4 hover:bg-gray-50">
                          <div className="flex items-start">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3 flex-shrink-0">
                              <ClipboardList size={16} />
                            </div>
                            <div>
                              <p className="text-sm text-gray-700">{log}</p>
                              <div className="mt-1 flex items-center text-xs text-gray-500">
                                <Clock size={12} className="mr-1" />
                                <span>
                                  {log.includes(" at ") ? log.split(" at ")[1] : ""}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-4">
                        <FileText size={24} />
                      </div>
                      <p className="text-gray-500 text-sm">No logs are available for this product</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Confirm Deletion"
        size="sm"
      >
        <div className="space-y-4 no-scrollbar">
          {selectedProduct && (
            <>
              <p className="text-gray-700">
                Are you sure you want to delete this product?
              </p>
              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                <h4 className="text-sm font-medium text-gray-800 mb-2">Product Details:</h4>
                <div className="text-sm space-y-1">
                  <p><span className="font-medium">Name:</span> {selectedProduct.name}</p>
                  <p><span className="font-medium">ID:</span> <span className="font-mono text-xs">{selectedProduct._id}</span></p>
                  <p><span className="font-medium">Brand:</span> {selectedProduct.brandName}</p>
                </div>
              </div>
              <p className="text-sm text-red-600 font-medium">This action cannot be undone.</p>
            </>
          )}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                console.log('Deleting product with ID:', selectedProduct?._id);
                handleDeleteConfirm();
              }}
            >
              Delete Product
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProductsPage; 